import { dayjs, formatDate } from '@/dayjs'
import {
	eventStateToLabelKey,
	resolveStartInstant,
	zoneAbbr
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeEventDetail, MarqueeEventListItem } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * Pill colors per state, matching the Figma "Badge Inline" tokens: pre-work
 * blue, locked amber (frozen, staged to start), live red, closed green. Only
 * `live` carries a leading dot in the design.
 */
const STATE_STYLE: Record<
	MarqueeEventListItem['state'],
	{ pill: string; dot?: string }
> = {
	pre_work: { pill: 'bg-blue-100 text-blue-700' },
	locked: { pill: 'bg-orange-600/15 text-orange-700' },
	live: { pill: 'bg-red-100 text-red-700', dot: 'bg-red-700' },
	closed: { pill: 'bg-green-100 text-green-700' }
}

/**
 * The fields the badge reads. Both read models satisfy it: `closed_at` only
 * exists on the detail, so the tracker card renders a plain "Closed".
 */
type BadgeEvent = Pick<MarqueeEventListItem, 'state' | 'date'> &
	Partial<Pick<MarqueeEventDetail, 'start_time' | 'time_zone' | 'closed_at'>>

interface EventStatusBadgeProps {
	event: BadgeEvent
	className?: string
}

/**
 * Lifecycle pill: the state label plus the detail that state cares about — a
 * countdown while the event is still ahead, the start instant once live, the
 * close instant once closed.
 *
 * Every word is composed here from i18n keys. The API also sends a ready-made
 * `state_badge` ("Pre-work · T-9d"), but it is English built server-side and
 * cannot be translated, so it is deliberately ignored.
 */
export function EventStatusBadge({ event, className }: EventStatusBadgeProps) {
	const { t } = useTranslation()
	const { pill, dot } = STATE_STYLE[event.state]

	/**
	 * The one place that keeps the zone abbreviation on the timestamp itself.
	 * The detail page states the zone once in its meta line, but this same pill
	 * also renders in the events list, where rows from different markets sit on
	 * top of each other with no header to disambiguate them.
	 */
	const zonedInstant = (
		iso: string | null | undefined
	): string | undefined => {
		if (!iso) {
			return undefined
		}
		const time = formatDate(iso, 'lll', event.time_zone)
		// `formatDate` says '-' for an unparseable instant. In a pill that would
		// read as "Closed · -", so the detail is dropped instead.
		if (time === '-') {
			return undefined
		}
		const abbr = zoneAbbr(iso, event.time_zone)
		return abbr ? `${time} ${abbr}` : time
	}

	const detail = (): string | undefined => {
		if (event.state === 'closed') {
			// List rows carry no `closed_at`; the pill is then just "Closed".
			return zonedInstant(event.closed_at)
		}
		if (event.state === 'live') {
			return zonedInstant(
				resolveStartInstant(
					event.start_time,
					event.time_zone,
					event.date
				)
			)
		}
		// pre_work / locked: how far out the event still is, in whole days.
		const days = dayjs(event.date)
			.startOf('day')
			.diff(dayjs().startOf('day'), 'day')
		if (days > 0) {
			return t('marquee:status_badge_countdown', { days })
		}
		if (days === 0) {
			return t('marquee:status_badge_today')
		}
		// The date has passed and the event never started: a countdown would
		// read backwards, so the pill says only where the event is stuck.
		return undefined
	}

	const label = [t(eventStateToLabelKey[event.state]), detail()]
		.filter(Boolean)
		.join(' · ')

	return (
		<div
			className={cn(
				'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-medium text-xs',
				pill,
				className
			)}
		>
			{dot && (
				<span className={cn('size-1.5 shrink-0 rounded-full', dot)} />
			)}
			<span className='whitespace-nowrap'>{label}</span>
		</div>
	)
}
